import type { GameInstanceConfig, GamePlugin, RoundContext } from '../types';
import {
  applyMoneyToNetOwed,
  appliesMoney,
  getMoneyAmount,
  getStakeConfig,
} from '../stakes-helpers';
import { perHolePuttsSettlement, totalPotSettlement } from '../stake-settlement';

function totalPutts(playerKey: string, ctx: RoundContext): number | null {
  const putts = ctx.putts?.[playerKey];
  if (!putts?.length) return null;
  let sum = 0;
  let count = 0;
  ctx.holes.forEach((h, i) => {
    const p = putts[i];
    if (p != null && Number.isFinite(p)) {
      sum += p;
      count += 1;
    }
  });
  if (count === 0) return null;
  return sum;
}

function lowestTotalSettlement(
  ctx: RoundContext,
  formatId: string,
  rows: Array<{ playerKey: string; name: string; val: number }>,
): Record<string, number> {
  const stake = getStakeConfig(ctx.stakes, formatId);
  if (!appliesMoney(stake) || !rows.length) return {};
  const amount = getMoneyAmount(stake) * 100;
  return totalPotSettlement(
    rows.map((r) => ({ playerKey: r.playerKey, val: r.val })),
    amount,
  );
}

function perPuttSettlement(
  ctx: RoundContext,
  formatId: string,
  rows: Array<{ playerKey: string; name: string; val: number }>,
): Record<string, number> {
  const stake = getStakeConfig(ctx.stakes, formatId);
  if (!appliesMoney(stake) || !rows.length) return {};
  const rate = getMoneyAmount(stake);
  const minPutts = rows[0].val;
  const raw: Record<string, number> = {};
  rows.forEach((r) => {
    raw[r.playerKey] = -Math.round((r.val - minPutts) * rate * 100);
  });
  const winnerKeys = rows.filter((r) => r.val === minPutts).map((r) => r.playerKey);
  const totalPaid = Object.values(raw).reduce((a, b) => a + b, 0);
  if (totalPaid !== 0 && winnerKeys.length) {
    const share = Math.floor(Math.abs(totalPaid) / winnerKeys.length);
    winnerKeys.forEach((k) => { raw[k] = (raw[k] || 0) + share; });
  }
  return raw;
}

export const puttsBetPlugin: GamePlugin = {
  id: 'putts-bet',
  label: 'Putts Bet',
  description: 'Fewest putts wins or pay per putt over the leader',
  requiresTeams: false,
  minPlayers: 2,
  defaultStakes: { scoringType: 'money', money: 5, puttsBetMode: 'lowest-total' },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'putts-bet');
    const mode = stake.puttsBetMode ?? 'lowest-total';

    const rows = ctx.players
      .map((p) => ({
        playerKey: p.playerKey,
        name: p.name,
        val: totalPutts(p.playerKey, ctx),
      }))
      .filter((r): r is { playerKey: string; name: string; val: number } => r.val != null)
      .sort((a, b) => a.val - b.val);

    if (!rows.length) {
      return {
        formatId: 'putts-bet',
        label: 'Putts Bet',
        summary: 'Enter putts on the scorecard to settle',
        netOwed: {},
      };
    }

    const leader = rows[0];
    const summary = mode === 'per-putt'
      ? `${leader.name} fewest putts (${leader.val}) · $${getMoneyAmount(stake)}/putt over leader`
      : mode === 'per-hole'
        ? `${leader.name} leads · $${getMoneyAmount(stake)}/hole won`
        : `${leader.name} leads with ${leader.val} putts`;

    const raw = mode === 'per-putt'
      ? perPuttSettlement(ctx, 'putts-bet', rows)
      : mode === 'per-hole'
        ? perHolePuttsSettlement(ctx, getMoneyAmount(stake) * 100)
        : lowestTotalSettlement(ctx, 'putts-bet', rows);

    return {
      formatId: 'putts-bet',
      label: 'Putts Bet',
      summary,
      standings: rows.map((r, i) => ({
        playerKey: r.playerKey,
        name: r.name,
        value: `${r.val} putts`,
        rank: i + 1,
      })),
      netOwed: applyMoneyToNetOwed(raw, stake),
      winners: [leader.playerKey],
    };
  },
};
